// ApolloAuthProvider.tsx
import React, { PropsWithChildren, useMemo } from "react";
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { useAuth0 } from "@auth0/auth0-react";
import { resolveTenant } from "./tenant";

type Props = {
    apiBase?: string; // optional override
};

export function ApolloAuthProvider({ children, apiBase }: PropsWithChildren<Props>) {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const tenant = resolveTenant();
    const apiUrl = `${apiBase ?? process.env.REACT_APP_BACK_API}/graphql`;

    const client = useMemo(() => {
        const httpLink = createHttpLink({ uri: apiUrl });

        const authLink = setContext(async (_, { headers }) => {
            const token = isAuthenticated ? await getAccessTokenSilently().catch(() => null) : null;
            return {
                headers: {
                    ...headers,
                    'X-Tenant': tenant,                              // 🔑 always send tenant to BE
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            };
        });

        return new ApolloClient({ link: authLink.concat(httpLink), cache: new InMemoryCache() });
    }, [apiUrl, getAccessTokenSilently, isAuthenticated, tenant]);

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
